"use client";

import { cn } from "@/lib/utils";
import { forwardRef } from "react";

export type HoverScaleSize = "sm" | "md" | "lg";

export interface HoverScaleProps {
  /** Scale amount on hover */
  scale?: HoverScaleSize;
  /** Whether to add shadow effect on hover */
  shadow?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Child elements */
  children: React.ReactNode;
  /** Click handler - adds cursor-pointer when provided */
  onClick?: () => void;
  /** Element type to render */
  as?: "div" | "button" | "article" | "section";
  /** Whether component is disabled (no hover effects) */
  disabled?: boolean;
}

const scaleClasses: Record<HoverScaleSize, string> = {
  sm: "hover:scale-[1.02]",
  md: "hover:scale-[1.05]",
  lg: "hover:scale-110",
};

/**
 * HoverScale - Interactive scale animation wrapper
 *
 * Wraps children with consistent hover:scale and transition animations.
 * Optionally adds shadow effects on hover.
 *
 * @example
 * // Card with hover effect
 * <HoverScale onClick={() => router.push('/details')} shadow>
 *   <Card>
 *     <CardContent>...</CardContent>
 *   </Card>
 * </HoverScale>
 *
 * // Subtle scale for list items
 * <HoverScale scale="sm">
 *   <ListItem>...</ListItem>
 * </HoverScale>
 */
export const HoverScale = forwardRef<HTMLDivElement, HoverScaleProps>(
  function HoverScale(
    {
      scale = "sm",
      shadow = false,
      className,
      children,
      onClick,
      as = "div",
      disabled = false,
    },
    ref
  ) {
    const Component = as;

    return (
      <Component
        ref={ref as any}
        className={cn(
          "transition-all duration-300",
          !disabled && scaleClasses[scale],
          !disabled && shadow && "hover:shadow-xl",
          onClick && !disabled && "cursor-pointer",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
        onClick={disabled ? undefined : onClick}
      >
        {children}
      </Component>
    );
  }
);

export default HoverScale;
